import { AutodataCustomEventTypes } from "../../autodata/src/ts/types";

import parseAppConfigHTMLAttribute from "@common/helpers/parseAppConfigHTMLAttribute";

interface AppConfig {
  wording: {
    allResults: string;
    noResult: string;
  };
  textInputDOMNodeSelector: string;
  seeAllResultsURL: string;
}

const allResultsURLFormatter = (seeAllResultsURL: string) => (
  searchText: string
): string => `${seeAllResultsURL}?faqSearchText=${searchText}`;

export default (domNode: HTMLElement): DisposableApp => {
  const appConfig: AppConfig = parseAppConfigHTMLAttribute(domNode);

  const getAllResultsPageURL = allResultsURLFormatter(
    appConfig.seeAllResultsURL
  );

  const textInputDOMNode = dom.querySelector(
    appConfig.textInputDOMNodeSelector
  ) as HTMLElement;

  const { searchInputId } = textInputDOMNode.dataset;

  const searchInputIconLinkDOMNode = dom.querySelector(".search-input__link");

  /**
   * Allow navigating to search results only if search widget is not
   * on search result pages.
   * @type {boolean}
   */
  const enableNavigationToResultsPage =
    typeof searchInputId === "string" && searchInputId !== "search-results";

  const onKeypressEventHandler = event => {
    // enter key
    if (event.keyCode === 13) {
      window.location.href = getAllResultsPageURL(event.target.value);
    }
  };

  const onSearchChanged = searchText => {
    /**
     * Change magnifying glass icon link after
     * every user text inputs.
     */
    searchInputIconLinkDOMNode!.setAttribute(
      "href",
      getAllResultsPageURL(searchText)
    );
  };

  const widgetConfig: WidgetConfigOptions = {
    theme: "@default",
    parentDOMNode: domNode,
    seeAllResultsURL: getAllResultsPageURL("{{search_text}}"),
    textInputDOMNode,
    getAlgoliaConfig: () =>
      Promise.resolve(window.dynamic_faq.search_widget_configuration),
    wording: appConfig.wording,
    onClickAllResults: () => {
      dispatchEvent(
        new CustomEvent(
          AutodataCustomEventTypes.ON_CLICK_ALL_RESULTS_WIDGET_LINK
        )
      );
    },
    onClickResult: (_, detail) => {
      dispatchEvent(
        new CustomEvent(AutodataCustomEventTypes.ON_CLICK_RESULT_WIDGET_LINK, {
          detail,
        })
      );
    },
    debug: true,
  };

  if (enableNavigationToResultsPage) {
    textInputDOMNode.addEventListener("keypress", onKeypressEventHandler);

    widgetConfig.onSearchChanged = onSearchChanged;
  }

  const widget = new SearchWidget(widgetConfig);

  return function disposeCallback() {
    widget.dispose();
  };
};
